import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  InsurerDashboardTab,
  insurerDashboardLegacyTabRedirect,
  insurerDashboardTabRequiresRedirect,
  parseInsurerDashboardTab,
} from '../utils/insurerDashboardTabs';
import { useInsurerHeaderActions } from '../contexts/InsurerHeaderActionsContext';
import { usePartnerTokenTiers } from '../contexts/PartnerTokenTiersContext';
import GrantTokenModal from '../components/Insurer/GrantTokenModal';
import RevokeTokenModal from '../components/Insurer/RevokeTokenModal';
import {
  insurerService,
  ClientSummary,
  ClientDetail,
  PartnerLedgerEntry,
  PartnerLedgerListResponse,
  TeamMember,
  InsurerOrgInfo,
  InsurerAnalytics,
  InvitePreview,
} from '../services/insurerService';

const InsurerDashboard: React.FC = () => {
  const { user } = useAuth();
  const { tokenTitles, refresh: refreshTiers } = usePartnerTokenTiers();
  const isOrgAdmin = user?.org_role === 'org_admin';
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab: InsurerDashboardTab = parseInsurerDashboardTab(searchParams, isOrgAdmin);
  const [org, setOrg] = useState<InsurerOrgInfo | null>(null);

  useEffect(() => {
    refreshTiers();
  }, [refreshTiers]);

  useEffect(() => {
    const legacyRedirect = insurerDashboardLegacyTabRedirect(searchParams);
    if (legacyRedirect) {
      navigate(legacyRedirect, { replace: true });
      return;
    }
    if (insurerDashboardTabRequiresRedirect(searchParams, isOrgAdmin)) {
      navigate('/insurer-dashboard', { replace: true });
    }
  }, [searchParams, isOrgAdmin, navigate]);

  const [clients, setClients] = useState<ClientSummary[]>([]);
  const [ledger, setLedger] = useState<PartnerLedgerEntry[]>([]);
  const [ledgerTotal, setLedgerTotal] = useState(0);
  const [ledgerTotals, setLedgerTotals] = useState<PartnerLedgerListResponse['totals'] | null>(null);
  const [ledgerStartDate, setLedgerStartDate] = useState('');
  const [ledgerEndDate, setLedgerEndDate] = useState('');
  const ledgerStartDateRef = useRef(ledgerStartDate);
  ledgerStartDateRef.current = ledgerStartDate;
  const ledgerEndDateRef = useRef(ledgerEndDate);
  ledgerEndDateRef.current = ledgerEndDate;
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const isInitialMount = useRef(true);
  const [team, setTeam] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [grantTokenClient, setGrantTokenClient] = useState<ClientSummary | null>(null);
  const [revokeTokenClient, setRevokeTokenClient] = useState<ClientSummary | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  // Filters & local state
  const [clientSearchQuery, setClientSearchQuery] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState<'all' | 'registered' | 'invited' | 'expired'>('all');
  const [activeClientMenuId, setActiveClientMenuId] = useState<number | null>(null);
  const [clientMenuPosition, setClientMenuPosition] = useState<{
    top?: number;
    bottom?: number;
    left: number;
  } | null>(null);
  const [ledgerStatusFilter, setLedgerStatusFilter] = useState<'all' | 'pending' | 'invoiced' | 'paid' | 'void'>('all');

  // Team tab state
  const [teamEmail, setTeamEmail] = useState('');
  const [teamName, setTeamName] = useState('');
  const [teamRole, setTeamRole] = useState<'org_member' | 'org_admin'>('org_member');
  const [teamSubmitting, setTeamSubmitting] = useState(false);
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [removeTeamConfirm, setRemoveTeamConfirm] = useState<{
    open: boolean;
    userId: number | null;
    email: string;
  }>({ open: false, userId: null, email: '' });
  const [resendingClientId, setResendingClientId] = useState<number | null>(null);
  const [resendingTeamId, setResendingTeamId] = useState<number | null>(null);

  // Settings tab state
  const [settingsName, setSettingsName] = useState('');
  const [settingsBillingEmail, setSettingsBillingEmail] = useState('');
  const [settingsInviteContactEmail, setSettingsInviteContactEmail] = useState('');
  const [settingsMfaPolicy, setSettingsMfaPolicy] = useState<'optional' | 'mandatory'>('optional');
  const [settingsSubmitting, setSettingsSubmitting] = useState(false);
  const [logoUploading, setLogoUploading] = useState(false);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [previewTab, setPreviewTab] = useState<'client' | 'team'>('client');

  const [analytics, setAnalytics] = useState<InsurerAnalytics | null>(null);
  const [testEmail, setTestEmail] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);
  const [clientDetail, setClientDetail] = useState<ClientDetail | null>(null);
  const [clientDetailLoading, setClientDetailLoading] = useState(false);
  const [invitePreviewClient, setInvitePreviewClient] = useState<InvitePreview | null>(null);
  const [invitePreviewTeam, setInvitePreviewTeam] = useState<InvitePreview | null>(null);
  const [transferAdminUserId, setTransferAdminUserId] = useState<number | ''>('');
  const [roleUpdatingId, setRoleUpdatingId] = useState<number | null>(null);
  const { setActions } = useInsurerHeaderActions();

  // Close menus on outside click or scroll / resize / escape
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.client-menu-container') && !target.closest('.client-menu-portal')) {
        setActiveClientMenuId(null);
        setClientMenuPosition(null);
      }
    };
    window.addEventListener('click', handleOutsideClick);
    return () => window.removeEventListener('click', handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!activeClientMenuId) return;
    const handleClose = () => {
      setActiveClientMenuId(null);
      setClientMenuPosition(null);
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    window.addEventListener('scroll', handleClose, true);
    window.addEventListener('resize', handleClose);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('scroll', handleClose, true);
      window.removeEventListener('resize', handleClose);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [activeClientMenuId]);

  useEffect(() => {
    setActions(null);
    return () => setActions(null);
  }, [setActions]);

  const loadClients = useCallback(async () => {
    const data = await insurerService.listClients();
    setClients(data.clients);
  }, []);

  const loadLedger = useCallback(async (start?: string, end?: string) => {
    setLedgerLoading(true);
    try {
      const data = await insurerService.listPartnerLedger(start, end);
      setLedger(data.entries);
      setLedgerTotal(data.totals?.outstanding_amount_cents ?? data.total_amount_cents);
      if (data.totals) {
        setLedgerTotals(data.totals);
      }
    } catch (err: any) {
      console.error('Failed to load partner ledger:', err);
    } finally {
      setLedgerLoading(false);
    }
  }, []);

  const loadTeam = useCallback(async () => {
    const data = await insurerService.listTeamMembers();
    setTeam(data);
  }, []);

  const loadAnalytics = useCallback(async () => {
    const data = await insurerService.getAnalytics();
    setAnalytics(data);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const tasks: Promise<unknown>[] = [
        insurerService.getOrg().then(setOrg),
        loadClients(),
      ];
      if (isOrgAdmin) {
        tasks.push(
          loadLedger(ledgerStartDateRef.current || undefined, ledgerEndDateRef.current || undefined),
          loadTeam(),
          loadAnalytics()
        );
      }
      const results = await Promise.allSettled(tasks);
      const failed = results.find((r) => r.status === 'rejected') as PromiseRejectedResult | undefined;
      if (failed) {
        const reason = failed.reason;
        setError(reason?.response?.data?.detail || reason?.message || 'Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  }, [isOrgAdmin, loadClients, loadLedger, loadTeam, loadAnalytics]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    if (!loading && isOrgAdmin && tab === 'billing') {
      loadLedger(ledgerStartDate || undefined, ledgerEndDate || undefined);
    }
  }, [ledgerStartDate, ledgerEndDate, isOrgAdmin, tab, loading, loadLedger]);

  useEffect(() => {
    if (org) {
      setSettingsName(org.name);
      setSettingsBillingEmail(org.billing_email || '');
      setSettingsInviteContactEmail(org.invite_contact_email || '');
      setSettingsMfaPolicy(org.mfa_policy || 'optional');
    }
  }, [org]);

  useEffect(() => {
    if (!org?.has_logo) {
      setLogoPreviewUrl(null);
      return;
    }

    let objectUrl: string | null = null;
    let cancelled = false;

    insurerService.getOrgLogoObjectUrl().then((url) => {
      if (cancelled) {
        if (url) URL.revokeObjectURL(url);
        return;
      }
      objectUrl = url;
      setLogoPreviewUrl(url);
    });

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [org?.has_logo, org?.id]);

  const openGrantTokenModal = (client: ClientSummary) => {
    if (client.credits_remaining > 0) {
      setError('This client already has an unused credit. They must use it before you grant another.');
      return;
    }
    setError(null);
    setGrantTokenClient(client);
  };

  const handleGrantTokenSuccess = async (successMessage: string) => {
    setMessage(successMessage);
    setGrantTokenClient(null);
    await refresh();
  };

  const handleInviteTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    setTeamSubmitting(true);
    setMessage(null);
    setError(null);
    try {
      const result = await insurerService.inviteTeamMember(teamEmail.trim(), teamName.trim() || undefined);
      setMessage(result.message);
      setTeamEmail('');
      setTeamName('');
      await loadTeam();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to invite team member');
    } finally {
      setTeamSubmitting(false);
    }
  };

  const handleRemoveTeamMember = async (userId: number) => {
    setRemovingId(userId);
    setMessage(null);
    setError(null);
    try {
      const result = await insurerService.removeTeamMember(userId);
      setMessage(result.message);
      setRemoveTeamConfirm({ open: false, userId: null, email: '' });
      await loadTeam();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to remove team member');
    } finally {
      setRemovingId(null);
    }
  };

  const handleConfirmRemoveTeamMember = async () => {
    if (!removeTeamConfirm.userId) return;
    await handleRemoveTeamMember(removeTeamConfirm.userId);
  };

  const handleResendClientInvite = async (client: ClientSummary) => {
    setResendingClientId(client.id);
    setMessage(null);
    setError(null);
    try {
      const result = await insurerService.resendClientInvite(client.id);
      setMessage(result.message);
      await loadClients();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to resend invitation');
    } finally {
      setResendingClientId(null);
    }
  };

  const handleResendTeamInvite = async (member: TeamMember) => {
    setResendingTeamId(member.id);
    setMessage(null);
    setError(null);
    try {
      const result = await insurerService.resendTeamInvite(member.id);
      setMessage(result.message);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to resend invitation');
    } finally {
      setResendingTeamId(null);
    }
  };

  const handleRevokeTokenSuccess = async (successMsg: string) => {
    setMessage(successMsg);
    setRevokeTokenClient(null);
    await refresh();
  };

  const handleUnlinkClient = async (client: ClientSummary) => {
    if (!window.confirm(`Unlink ${client.email} from your organization?`)) return;
    setMessage(null);
    setError(null);
    try {
      const result = await insurerService.unlinkClient(client.id);
      setMessage(result.message);
      await refresh();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to unlink client');
    }
  };

  const handleSaveSettings = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOrgAdmin) return;

    setSettingsSubmitting(true);
    setMessage(null);
    setError(null);
    try {
      const updated = await insurerService.updateOrg({
        name: settingsName.trim(),
        billing_email: settingsBillingEmail.trim() || undefined,
        invite_contact_email: settingsInviteContactEmail.trim() || undefined,
        mfa_policy: settingsMfaPolicy,
      });
      setOrg(updated);
      setMessage('Organization settings updated');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to update settings');
    } finally {
      setSettingsSubmitting(false);
    }
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setLogoUploading(true);
    setMessage(null);
    setError(null);
    try {
      const updated = await insurerService.uploadOrgLogo(file);
      setOrg(updated);
      setMessage('Organization logo updated');
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const formatCents = (cents: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);

  const formatExerciseStatus = (status: string) => {
    const labels: Record<string, string> = {
      draft: 'Draft',
      scheduled: 'Scheduled',
      in_progress: 'In progress',
      completed: 'Completed',
      submitted_for_audit: 'Submitted for audit',
      cancelled: 'Cancelled',
    };
    return labels[status] ?? status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatLedgerStatus = (status: string) => {
    const labels: Record<string, string> = {
      pending_invoice: 'Pending invoice',
      invoiced: 'Invoiced',
      paid: 'Paid',
      void: 'Void',
    };
    return labels[status] ?? status.replace(/_/g, ' ');
  };

  const formatLedgerEntryType = (entryType?: string | null) => {
    const labels: Record<string, string> = {
      credit_grant: 'Token grant',
      audit_request: 'ISO audit fee',
    };
    return labels[entryType ?? ''] ?? 'Charge';
  };

  const formatTokenTier = (tier?: string | null) => {
    const key = tier ?? 'simple';
    return tokenTitles[key] ?? {
      simple: 'Prepared',
      gold: 'Prepared+',
      premium: 'Executive',
    }[key] ?? 'Prepared';
  };

  const formatPurchaseStatus = (status: string) => {
    const labels: Record<string, string> = {
      paid: 'Paid',
      pending: 'Pending',
      refunded: 'Refunded',
    };
    return labels[status] ?? status.replace(/_/g, ' ');
  };

  const formatShortDate = (iso: string) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const isInviteExpired = (client: ClientSummary) =>
    !client.is_registered &&
    Boolean(client.invitation_expires_at && new Date(client.invitation_expires_at).getTime() < Date.now());

  const openClientDetail = async (clientId: number) => {
    setSelectedClientId(clientId);
    setClientDetailLoading(true);
    setClientDetail(null);
    try {
      const detail = await insurerService.getClientDetail(clientId);
      setClientDetail(detail);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load client detail');
      setSelectedClientId(null);
    } finally {
      setClientDetailLoading(false);
    }
  };

  const loadInvitePreviews = async () => {
    try {
      const [clientPrev, teamPrev] = await Promise.all([
        insurerService.getInvitePreview('client'),
        insurerService.getInvitePreview('team'),
      ]);
      setInvitePreviewClient(clientPrev);
      setInvitePreviewTeam(teamPrev);
    } catch {
      /* optional */
    }
  };

  useEffect(() => {
    if (tab === 'settings' && isOrgAdmin) {
      loadInvitePreviews();
    }
  }, [tab, isOrgAdmin]);

  const handleTeamRoleChange = async (member: TeamMember, newRole: 'org_admin' | 'org_member') => {
    if (member.org_role === newRole) return;
    if (
      !window.confirm(
        `Change ${member.email} to ${newRole === 'org_admin' ? 'organization admin' : 'member'}?`
      )
    ) {
      return;
    }
    setRoleUpdatingId(member.id);
    setError(null);
    try {
      await insurerService.updateTeamMemberRole(member.id, newRole);
      setMessage('Team member role updated');
      await loadTeam();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to update role');
    } finally {
      setRoleUpdatingId(null);
    }
  };

  const handleTransferAdmin = async () => {
    if (transferAdminUserId === '') return;
    if (
      !window.confirm(
        'Transfer organization admin to this member? You will become a regular member.'
      )
    ) {
      return;
    }
    setError(null);
    try {
      await insurerService.transferOrgAdmin(transferAdminUserId);
      setMessage('Organization admin transferred. Refresh or log in again if your access changed.');
      setTransferAdminUserId('');
      await refresh();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Transfer failed');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0c1e40]" />
      </div>
    );
  }

  const isSuspended =
    org?.status === 'suspended' || error?.toLowerCase().includes('suspended');
  if (isSuspended) {
    return (
      <div className="min-h-screen bg-[#f8fafc] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-xl border border-gray-200 shadow p-8 text-center">
          <h1 className="text-xl font-bold text-[#0c1e40] mb-2">Organization suspended</h1>
          <p className="text-sm text-gray-600 mb-4">
            {org?.name ? `${org.name} has been suspended.` : 'Your organization has been suspended.'}{' '}
            Contact Flash AI™ support at support@flashprepared.com for assistance.
          </p>
        </div>
      </div>
    );
  }

  // Filter clients
  const expiredClients = clients.filter(isInviteExpired);
  const filteredClients = clients.filter((client) => {
    const matchesSearch =
      !clientSearchQuery.trim() ||
      client.email.toLowerCase().includes(clientSearchQuery.toLowerCase()) ||
      Boolean(client.full_name && client.full_name.toLowerCase().includes(clientSearchQuery.toLowerCase()));
    if (!matchesSearch) return false;

    if (clientStatusFilter === 'registered') return client.is_registered;
    if (clientStatusFilter === 'expired') return isInviteExpired(client);
    if (clientStatusFilter === 'invited') return !client.is_registered && !isInviteExpired(client);
    return true;
  });

  // Calculate billing totals
  const pendingCents = ledgerTotals?.pending_amount_cents ?? ledger.filter((e) => e.status === 'pending_invoice').reduce((s, e) => s + e.amount_cents, 0);
  const invoicedCents = ledgerTotals?.invoiced_amount_cents ?? ledger.filter((e) => e.status === 'invoiced').reduce((s, e) => s + e.amount_cents, 0);
  const paidCents = ledgerTotals?.paid_amount_cents ?? ledger.filter((e) => e.status === 'paid').reduce((s, e) => s + e.amount_cents, 0);
  const voidedCents = ledgerTotals?.void_amount_cents ?? ledger.filter((e) => e.status === 'void').reduce((s, e) => s + e.amount_cents, 0);
  const outstandingCents = ledgerTotals?.outstanding_amount_cents ?? ledgerTotal;

  const filteredLedger = ledger.filter((e) => {
    if (ledgerStatusFilter === 'all') return true;
    if (ledgerStatusFilter === 'pending') return e.status === 'pending_invoice';
    return e.status === ledgerStatusFilter;
  });

  return (
    <div className="min-h-screen bg-[#f8fafc] text-slate-900 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
        {!isOrgAdmin && tab === 'clients' && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-900 shadow-sm">
            View-only access — contact your organization admin to invite clients or change settings.
          </div>
        )}

        {error && !isSuspended && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 shadow-sm flex items-center justify-between">
            <span>{error}</span>
            <button type="button" onClick={() => setError(null)} className="text-red-500 hover:text-red-700 font-bold ml-4">
              &times;
            </button>
          </div>
        )}
        {message && (
          <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800 shadow-sm flex items-center justify-between">
            <span>{message}</span>
            <button type="button" onClick={() => setMessage(null)} className="text-emerald-500 hover:text-emerald-700 font-bold ml-4">
              &times;
            </button>
          </div>
        )}

        {/* ───────────── CLIENTS TAB (SCREENSHOT 1) ───────────── */}
        {tab === 'clients' && (
          <div>
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-3xl font-extrabold text-[#0c1e40] tracking-tight">Clients</h1>
                <p className="text-sm text-gray-500 mt-1">
                  Invite policyholders to Flash and fund their tabletop exercises with credits.
                </p>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => insurerService.downloadClientsCsv()}
                  className="px-4 py-2 text-sm font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-colors"
                >
                  Export CSV
                </button>
                {isOrgAdmin && (
                  <Link
                    to="/bulk-invite"
                    className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-lg bg-[#991b1b] text-white hover:bg-[#7f1d1d] shadow-sm transition-colors"
                  >
                    <span className="text-base font-bold leading-none">+</span> Invite clients
                  </Link>
                )}
              </div>
            </div>

            {/* 4-Metric Banner */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-6 mb-6">
              <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-200">
                <div className="pb-4 md:pb-0 md:px-4 first:pl-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <span className="text-gray-400 font-normal mr-1">1</span> Invited
                  </p>
                  <p className="text-3xl font-bold text-[#0c1e40] mt-1">
                    {analytics?.clients_total ?? clients.length}
                  </p>
                </div>
                <div className="pt-4 md:pt-0 md:px-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <span className="text-gray-400 font-normal mr-1">2</span> Registered
                  </p>
                  <p className="text-3xl font-bold text-[#0c1e40] mt-1">
                    {analytics?.clients_registered ?? clients.filter((c) => c.is_registered).length}
                  </p>
                </div>
                <div className="pt-4 md:pt-0 md:px-4">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <span className="text-gray-400 font-normal mr-1">3</span> Credits granted
                  </p>
                  <p className="text-3xl font-bold text-[#0c1e40] mt-1">
                    {analytics?.credits_granted_mtd ?? 0}
                  </p>
                </div>
                <div className="pt-4 md:pt-0 md:px-4 last:pr-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    <span className="text-gray-400 font-normal mr-1">4</span> Exercises completed
                  </p>
                  <p className="text-3xl font-bold text-[#0c1e40] mt-1">
                    {analytics?.exercises_completed ??
                      clients.reduce((acc, c) => acc + (c.exercises_used || 0), 0)}
                  </p>
                </div>
              </div>
            </div>

            {/* Expired Invite Warning Banner */}
            {expiredClients.length > 0 && (
              <div className="rounded-xl border border-amber-200 bg-[#fef3c7]/40 p-4 mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 shadow-sm">
                <div className="flex items-center gap-2.5 text-sm text-amber-900">
                  <svg className="w-5 h-5 text-amber-700 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>
                    <strong className="font-semibold text-amber-950">
                      {expiredClients.length} {expiredClients.length === 1 ? 'invite has' : 'invites have'} expired.
                    </strong>{' '}
                    The client never registered, so they can&apos;t start an exercise yet.
                  </span>
                </div>
                {isOrgAdmin && (
                  <button
                    type="button"
                    onClick={() => handleResendClientInvite(expiredClients[0])}
                    disabled={resendingClientId !== null}
                    className="shrink-0 px-4 py-2 text-sm font-semibold rounded-lg border border-amber-300 bg-white text-amber-900 hover:bg-amber-50 shadow-sm disabled:opacity-50 transition-colors"
                  >
                    {resendingClientId === expiredClients[0].id ? 'Sending…' : 'Resend invite'}
                  </button>
                )}
              </div>
            )}

            {/* Search and Filters Bar */}
            <div className="bg-white border border-gray-200 rounded-t-xl p-3 shadow-sm flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 border-b-0">
              <div className="relative flex-1 max-w-xl">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={clientSearchQuery}
                  onChange={(e) => setClientSearchQuery(e.target.value)}
                  placeholder="Search by email or company"
                  className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-transparent"
                />
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-600">Status</span>
                <select
                  value={clientStatusFilter}
                  onChange={(e) => setClientStatusFilter(e.target.value as any)}
                  className="text-sm font-medium border border-gray-200 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                >
                  <option value="all">All</option>
                  <option value="registered">Registered</option>
                  <option value="invited">Invited</option>
                  <option value="expired">Invite expired</option>
                </select>
              </div>
            </div>

            {/* Client Table */}
            <div className="bg-white border border-gray-200 rounded-b-xl shadow-sm overflow-hidden overflow-x-auto min-h-[160px]">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-white border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Invite</th>
                    <th className="px-6 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Credits</th>
                    <th className="px-6 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Exercises</th>
                    <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {filteredClients.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-500">
                        {clients.length === 0
                          ? 'No clients yet. Invite policyholders to fund tabletop exercises with credits.'
                          : 'No clients match your filter criteria.'}
                      </td>
                    </tr>
                  ) : (
                    filteredClients.map((client) => {
                      const expired = isInviteExpired(client);
                      const initial = (client.full_name || client.email || 'U').charAt(0).toUpperCase();
                      const isMenuOpen = activeClientMenuId === client.id;

                      return (
                        <tr key={client.id} className="hover:bg-slate-50/60 transition-colors">
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 rounded-full bg-blue-50 text-[#0c1e40] font-bold flex items-center justify-center text-sm shrink-0 border border-blue-100">
                                {initial}
                              </div>
                              <div className="min-w-0">
                                <div className="text-sm font-bold text-[#0c1e40] truncate">{client.email}</div>
                                <div className="text-xs text-gray-500 truncate">
                                  {client.full_name || (client.is_registered ? 'Registered client' : 'Not registered yet')}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {client.is_registered ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-600"></span>
                                Registered
                              </span>
                            ) : expired ? (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-[#fef2f2] text-[#991b1b] border border-red-100">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#991b1b]"></span>
                                Invite expired
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-600"></span>
                                Invited
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-xs">
                            <div className="text-gray-700">
                              Sent {client.last_invited_at ? formatShortDate(client.last_invited_at) : formatShortDate(client.created_at)}
                            </div>
                            {!client.is_registered && client.invitation_expires_at && (
                              <div className={expired ? 'font-semibold text-[#991b1b] mt-0.5' : 'text-gray-500 mt-0.5'}>
                                {expired ? 'Expired ' : 'Expires '}
                                {formatShortDate(client.invitation_expires_at)}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-[#0c1e40]">
                            {client.credits_remaining}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-[#0c1e40]">
                            {client.exercises_used}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right">
                            {isOrgAdmin ? (
                              <div className="flex items-center justify-end gap-2 client-menu-container relative">
                                {!client.is_registered && (
                                  <button
                                    type="button"
                                    disabled={resendingClientId === client.id}
                                    onClick={() => handleResendClientInvite(client)}
                                    className="px-3.5 py-1.5 text-xs font-semibold rounded-lg bg-[#0c1e40] text-white hover:bg-[#1e293b] shadow-sm disabled:opacity-50 transition-colors"
                                  >
                                    {resendingClientId === client.id ? 'Sending…' : 'Resend invite'}
                                  </button>
                                )}
                                <button
                                  type="button"
                                  disabled={client.credits_remaining > 0}
                                  onClick={() => openGrantTokenModal(client)}
                                  title={client.credits_remaining > 0 ? 'Client must use existing credit first' : 'Grant tabletop credit'}
                                  className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-[#991b1b] text-[#991b1b] bg-white hover:bg-red-50 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  Grant credit
                                </button>
                                <div className="relative inline-block text-left">
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (activeClientMenuId === client.id) {
                                        setActiveClientMenuId(null);
                                        setClientMenuPosition(null);
                                      } else {
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        const spaceBelow = window.innerHeight - rect.bottom;
                                        const showAbove = spaceBelow < 150 && rect.top > 150;
                                        const left = Math.max(10, Math.min(rect.right - 176, window.innerWidth - 186));
                                        setClientMenuPosition(
                                          showAbove
                                            ? { bottom: window.innerHeight - rect.top + 4, left }
                                            : { top: rect.bottom + 4, left }
                                        );
                                        setActiveClientMenuId(client.id);
                                      }
                                    }}
                                    className={`p-1.5 rounded-lg border transition-colors ${
                                      isMenuOpen
                                        ? 'text-[#0c1e40] bg-gray-100 border-gray-400'
                                        : 'text-gray-500 hover:text-gray-700 border-gray-300 hover:bg-gray-50'
                                    }`}
                                    title="More options"
                                  >
                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                      <path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM16 12a2 2 0 100-4 2 2 0 000 4z" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={() => openClientDetail(client.id)}
                                className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                              >
                                View
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ───────────── CREDITS & BILLING TAB (SCREENSHOT 2) ───────────── */}
        {tab === 'billing' && (
          <div>
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-extrabold text-[#0c1e40] tracking-tight">Credits &amp; billing</h1>
              <p className="text-sm text-gray-500 mt-1">
                Flash invoices {org?.name || 'your organization'} for the credits you grant to clients.
              </p>
            </div>

            {/* Top 2-Column Summary Cards */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Card 1: Amount outstanding */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-600">Amount outstanding</span>
                    {outstandingCents === 0 ? (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                        <span className="font-bold">✓</span> Nothing due
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                        Payment pending
                      </span>
                    )}
                  </div>
                  <div className="text-4xl font-extrabold text-[#0c1e40] my-4">
                    {formatCents(outstandingCents)}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-left">
                    <div className="bg-[#f1f5f9] rounded-xl p-3">
                      <p className="text-xs text-gray-500 font-medium">Pending</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{formatCents(pendingCents)}</p>
                    </div>
                    <div className="bg-[#f1f5f9] rounded-xl p-3">
                      <p className="text-xs text-gray-500 font-medium">Invoiced</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{formatCents(invoicedCents)}</p>
                    </div>
                    <div className="bg-[#f1f5f9] rounded-xl p-3">
                      <p className="text-xs text-gray-500 font-medium">Paid</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{formatCents(paidCents)}</p>
                    </div>
                    <div className="bg-[#f1f5f9] rounded-xl p-3">
                      <p className="text-xs text-gray-500 font-medium">Voided</p>
                      <p className="text-sm font-bold text-gray-900 mt-0.5">{formatCents(voidedCents)}</p>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-4">
                  Outstanding = pending + invoiced. Questions about an invoice?{' '}
                  <a href="mailto:billing@flashprepared.com" className="text-blue-600 font-medium hover:underline">
                    Contact billing
                  </a>
                </p>
              </div>

              {/* Card 2: Credits Breakdown */}
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center justify-between pb-4 border-b border-gray-100">
                    <h2 className="text-lg font-bold text-[#0c1e40]">Credits</h2>
                    <button
                      type="button"
                      onClick={() => navigate('/insurer-dashboard?tab=clients')}
                      className="text-sm font-semibold text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                    >
                      Grant credit &rarr;
                    </button>
                  </div>
                  <div className="divide-y divide-gray-100 mt-2">
                    <div className="py-3.5 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Granted this month</span>
                      <span className="text-sm font-bold text-[#0c1e40]">
                        {analytics?.credits_granted_mtd ?? 0}
                      </span>
                    </div>
                    <div className="py-3.5 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Unused (held by clients)</span>
                      <span className="text-sm font-bold text-[#0c1e40]">
                        {analytics?.unused_credits ?? 0}
                      </span>
                    </div>
                    <div className="py-3.5 flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Used on completed exercises</span>
                      <span className="text-sm font-bold text-[#0c1e40]">
                        {analytics?.exercises_completed ?? 0}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-4">
                  Credits are deducted once a client initiates their tabletop exercise.
                </p>
              </div>
            </div>

            {/* Credit History Card */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-4 sm:p-6 border-b border-gray-200 flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                <div className="flex items-center gap-2.5">
                  <h3 className="text-base font-bold text-[#0c1e40]">Credit history</h3>
                  {!ledgerStartDate && !ledgerEndDate ? (
                    <span className="text-[11px] font-medium text-gray-500 bg-gray-100 px-2.5 py-0.5 rounded-full">
                      Recent 12
                    </span>
                  ) : (
                    <span className="text-[11px] font-medium text-blue-700 bg-blue-50 px-2.5 py-0.5 rounded-full border border-blue-100">
                      Filtered
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap items-center gap-2.5 sm:gap-3">
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="ledger-from-date" className="text-xs font-semibold text-gray-600">
                      From
                    </label>
                    <input
                      id="ledger-from-date"
                      type="date"
                      aria-label="Filter from date"
                      value={ledgerStartDate}
                      onChange={(e) => setLedgerStartDate(e.target.value)}
                      className="text-xs font-medium border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-[#0c1e40] cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="ledger-to-date" className="text-xs font-semibold text-gray-600">
                      To
                    </label>
                    <input
                      id="ledger-to-date"
                      type="date"
                      aria-label="Filter to date"
                      value={ledgerEndDate}
                      onChange={(e) => setLedgerEndDate(e.target.value)}
                      className="text-xs font-medium border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-[#0c1e40] cursor-pointer"
                    />
                  </div>
                  <div className="flex items-center gap-1.5">
                    <label htmlFor="ledger-status-filter" className="text-xs font-semibold text-gray-600">
                      Status
                    </label>
                    <select
                      id="ledger-status-filter"
                      aria-label="Filter by status"
                      value={ledgerStatusFilter}
                      onChange={(e) => setLedgerStatusFilter(e.target.value as any)}
                      className="text-xs font-medium border border-gray-300 rounded-lg px-2.5 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0c1e40] focus:border-[#0c1e40] cursor-pointer"
                    >
                      <option value="all">All</option>
                      <option value="pending">Pending</option>
                      <option value="invoiced">Invoiced</option>
                      <option value="paid">Paid</option>
                      <option value="void">Voided</option>
                    </select>
                  </div>
                  {(Boolean(ledgerStartDate || ledgerEndDate || ledgerStatusFilter !== 'all')) && (
                    <button
                      type="button"
                      onClick={() => {
                        setLedgerStartDate('');
                        setLedgerEndDate('');
                        setLedgerStatusFilter('all');
                      }}
                      title="Clear all filters"
                      className="px-2.5 py-1.5 text-xs font-semibold text-slate-600 hover:text-red-600 bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-200 rounded-lg transition-colors cursor-pointer"
                    >
                      Reset
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() =>
                      insurerService.downloadLedgerCsv(
                        ledgerStartDate || undefined,
                        ledgerEndDate || undefined
                      )
                    }
                    className="px-3.5 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm transition-colors cursor-pointer"
                  >
                    Export CSV
                  </button>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Client</th>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Credits</th>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                    </tr>
                  </thead>
                  <tbody className={`divide-y divide-gray-100 bg-white transition-opacity ${ledgerLoading ? 'opacity-50' : ''}`}>
                    {filteredLedger.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-6 py-12 text-center text-sm text-gray-500">
                          {ledgerLoading ? (
                            <span className="inline-flex items-center gap-2">
                              <span className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                              Loading credit records...
                            </span>
                          ) : (
                            'No credit ledger records found.'
                          )}
                        </td>
                      </tr>
                    ) : (
                      filteredLedger.map((entry) => {
                        const isVoid = entry.status === 'void';
                        return (
                          <tr key={entry.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                              {formatShortDate(entry.created_at)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              {entry.client_email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-[#0c1e40]">
                              1
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={isVoid ? 'line-through text-gray-400 font-medium' : 'font-bold text-[#0c1e40]'}>
                                {formatCents(entry.amount_cents)}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs">
                              {isVoid ? (
                                <div>
                                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                    Voided
                                  </span>
                                  <div className="text-gray-500 text-[11px] mt-0.5">Won&apos;t be invoiced</div>
                                </div>
                              ) : entry.status === 'paid' ? (
                                <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                                  Paid
                                </span>
                              ) : entry.status === 'invoiced' ? (
                                <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-100">
                                  Invoiced
                                </span>
                              ) : (
                                <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-100">
                                  Pending invoice
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ───────────── TEAM TAB (SCREENSHOT 3) ───────────── */}
        {tab === 'team' && (
          <div className="space-y-8">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-extrabold text-[#0c1e40] tracking-tight">Team</h1>
              <p className="text-sm text-gray-500 mt-1">
                People at {org?.name || 'your organization'} who can manage clients and credits.
              </p>
            </div>

            {/* Card 1: Invite a Teammate */}
            {isOrgAdmin ? (
              <form onSubmit={handleInviteTeam} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
                <h2 className="text-base font-bold text-[#0c1e40] mb-4">Invite a teammate</h2>
                <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1.5fr_1fr_auto] gap-4 items-end">
                  <div>
                    <label htmlFor="team-work-email" className="block text-xs font-semibold text-gray-700 mb-1">
                      Work email
                    </label>
                    <input
                      id="team-work-email"
                      type="email"
                      required
                      value={teamEmail}
                      onChange={(e) => setTeamEmail(e.target.value)}
                      placeholder="colleague@example.com"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                    />
                  </div>
                  <div>
                    <label htmlFor="team-name" className="block text-xs font-semibold text-gray-700 mb-1">
                      Name <span className="text-gray-400 font-normal">Optional</span>
                    </label>
                    <input
                      id="team-name"
                      type="text"
                      value={teamName}
                      onChange={(e) => setTeamName(e.target.value)}
                      placeholder="Jane Doe"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                    />
                  </div>
                  <div>
                    <label htmlFor="team-role" className="block text-xs font-semibold text-gray-700 mb-1">
                      Role
                    </label>
                    <select
                      id="team-role"
                      value={teamRole}
                      onChange={(e) => setTeamRole(e.target.value as any)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                    >
                      <option value="org_member">Member</option>
                      <option value="org_admin">Admin</option>
                    </select>
                  </div>
                  <div>
                    <button
                      type="submit"
                      disabled={teamSubmitting || !teamEmail.trim()}
                      className="w-full sm:w-auto px-5 py-2 text-sm font-semibold rounded-lg bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:opacity-50 transition-colors shadow-sm"
                    >
                      {teamSubmitting ? 'Sending…' : 'Send invite'}
                    </button>
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Members manage clients and credits. Admins can also change settings and the team. Invites expire after 7 days.
                </p>
              </form>
            ) : (
              <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm text-sm text-gray-600">
                Only organization admins can invite or remove team members.
              </div>
            )}

            {/* Card 2: Members List */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="p-6 pb-4 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-base font-bold text-[#0c1e40]">
                  Members <span className="font-semibold text-gray-500 ml-1">{team.length}</span>
                </h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-white border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                      <th className="px-6 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3.5 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {team.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="px-6 py-12 text-center text-sm text-gray-500">
                          No team members yet.
                        </td>
                      </tr>
                    ) : (
                      team.map((member) => {
                        const isSelf = member.id === user?.id;
                        const initial = (member.full_name || member.email).charAt(0).toUpperCase();
                        const isMemberAdmin = member.org_role === 'org_admin';
                        const adminCount = team.filter((m) => m.org_role === 'org_admin').length;
                        const isSoleAdmin = isMemberAdmin && adminCount <= 1;

                        return (
                          <tr key={member.id} className="hover:bg-slate-50/60 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-[#0c1e40] text-white font-bold flex items-center justify-center text-sm shrink-0">
                                  {initial}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm font-bold text-[#0c1e40]">
                                      {member.full_name || member.email}
                                    </span>
                                    {isSelf && (
                                      <span className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-md font-medium">
                                        You
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-xs text-gray-500">{member.email}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {isOrgAdmin && !isSelf && !isSoleAdmin ? (
                                <select
                                  value={member.org_role || 'org_member'}
                                  disabled={roleUpdatingId === member.id}
                                  onChange={(e) =>
                                    handleTeamRoleChange(member, e.target.value as 'org_admin' | 'org_member')
                                  }
                                  className="text-xs font-semibold border border-gray-200 rounded-lg px-2.5 py-1 text-gray-800 bg-white"
                                >
                                  <option value="org_member">Member</option>
                                  <option value="org_admin">Admin</option>
                                </select>
                              ) : (
                                <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-[#0c1e40] text-white">
                                  {isMemberAdmin ? 'Admin' : 'Member'}
                                </span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-xs">
                              {member.is_active && member.is_verified ? (
                                <div>
                                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-100">
                                    Active
                                  </span>
                                  <div className="text-gray-500 text-[11px] mt-0.5">
                                    Joined {formatShortDate(member.created_at)}
                                  </div>
                                </div>
                              ) : (
                                <div>
                                  <span className="inline-flex px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-50 text-amber-700 border border-amber-100">
                                    Invited
                                  </span>
                                  <div className="text-gray-500 text-[11px] mt-0.5">
                                    Expires {formatShortDate(member.invitation_expires_at || member.created_at)}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-xs">
                              {isSoleAdmin ? (
                                <span className="text-gray-500">
                                  Only admin &bull;{' '}
                                  <Link
                                    to="/insurer-dashboard?tab=settings"
                                    className="text-blue-600 font-medium hover:underline"
                                  >
                                    transfer in Settings
                                  </Link>
                                </span>
                              ) : isOrgAdmin ? (
                                <div className="flex items-center justify-end gap-2">
                                  {!(member.is_active && member.is_verified) && (
                                    <button
                                      type="button"
                                      disabled={resendingTeamId === member.id}
                                      onClick={() => handleResendTeamInvite(member)}
                                      className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                    >
                                      {resendingTeamId === member.id ? 'Sending…' : 'Resend invite'}
                                    </button>
                                  )}
                                  {!isSelf && (
                                    <button
                                      type="button"
                                      disabled={removingId === member.id}
                                      onClick={() =>
                                        setRemoveTeamConfirm({ open: true, userId: member.id, email: member.email })
                                      }
                                      className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-50"
                                    >
                                      {removingId === member.id ? 'Removing…' : 'Remove'}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400">—</span>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ───────────── SETTINGS TAB (SCREENSHOT 5) ───────────── */}
        {tab === 'settings' && isOrgAdmin && (
          <form onSubmit={handleSaveSettings} className="space-y-6 max-w-5xl">
            {/* Header */}
            <div>
              <h1 className="text-3xl font-extrabold text-[#0c1e40] tracking-tight">Organization settings</h1>
              <p className="text-sm text-gray-500 mt-1">
                These apply to everyone at {org?.name || 'your organization'} and the clients you invite.
              </p>
            </div>

            {/* Section 1: Organization */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h2 className="text-base font-bold text-[#0c1e40]">Organization</h2>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  How your company appears in Flash and on invoices.
                </p>
              </div>
              <div className="md:col-span-2 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="settings-org-name" className="block text-xs font-semibold text-gray-700 mb-1">
                      Organization name
                    </label>
                    <input
                      id="settings-org-name"
                      type="text"
                      required
                      value={settingsName}
                      onChange={(e) => setSettingsName(e.target.value)}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                    />
                  </div>
                  <div>
                    <label htmlFor="settings-billing-email" className="block text-xs font-semibold text-gray-700 mb-1">
                      Billing email
                    </label>
                    <input
                      id="settings-billing-email"
                      type="email"
                      value={settingsBillingEmail}
                      onChange={(e) => setSettingsBillingEmail(e.target.value)}
                      placeholder="billing@company.com"
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                    />
                  </div>
                </div>

                <div className="pt-2 flex items-center gap-4">
                  <div className="w-24 h-16 rounded-lg border-2 border-dashed border-gray-200 flex items-center justify-center bg-slate-50 text-xs text-gray-400 overflow-hidden shrink-0">
                    {logoPreviewUrl ? (
                      <img src={logoPreviewUrl} alt="Logo" className="w-full h-full object-contain p-1" />
                    ) : (
                      'No logo'
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-xs font-semibold text-gray-900">Logo</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Shown on your analytics dashboard and reports. PNG, JPEG or WebP, up to 512 KB.
                    </p>
                  </div>
                  <div>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      disabled={logoUploading}
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <button
                      type="button"
                      disabled={logoUploading}
                      onClick={() => logoInputRef.current?.click()}
                      className="px-4 py-2 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50 transition-colors"
                    >
                      {logoUploading ? 'Uploading…' : 'Upload logo'}
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 2: Client invitations */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h2 className="text-base font-bold text-[#0c1e40]">Client invitations</h2>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  When clients reply to an invite, it goes to this address.
                </p>
              </div>
              <div className="md:col-span-2 space-y-4">
                <div>
                  <label htmlFor="settings-reply-email" className="block text-xs font-semibold text-gray-700 mb-1">
                    Reply-to email
                  </label>
                  <input
                    id="settings-reply-email"
                    type="email"
                    value={settingsInviteContactEmail}
                    onChange={(e) => setSettingsInviteContactEmail(e.target.value)}
                    placeholder="clients@example.com"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">Leave blank to use your billing email.</p>
                </div>

                {/* Email Preview Box */}
                <div className="border border-gray-200 rounded-xl overflow-hidden bg-white mt-4">
                  <div className="border-b border-gray-200 bg-slate-50 px-4 flex gap-4">
                    <button
                      type="button"
                      onClick={() => setPreviewTab('client')}
                      className={`py-2 text-xs font-semibold border-b-2 transition-colors ${
                        previewTab === 'client'
                          ? 'border-[#991b1b] text-[#991b1b]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Client invite
                    </button>
                    <button
                      type="button"
                      onClick={() => setPreviewTab('team')}
                      className={`py-2 text-xs font-semibold border-b-2 transition-colors ${
                        previewTab === 'team'
                          ? 'border-[#991b1b] text-[#991b1b]'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      Team invite
                    </button>
                  </div>
                  <div className="p-4 text-xs text-gray-700 space-y-2">
                    {previewTab === 'client' ? (
                      <>
                        <div className="flex gap-2">
                          <span className="text-gray-400 font-medium">Subject</span>
                          <span className="font-bold text-[#0c1e40]">
                            {invitePreviewClient?.subject || `${settingsName || org?.name || 'Your Insurer'} invited you to Flash Emergency Management`}
                          </span>
                        </div>
                        <div className="flex gap-2">
                          <span className="text-gray-400 font-medium">Reply-to</span>
                          <span className="text-gray-600">
                            {settingsInviteContactEmail || settingsBillingEmail || 'billing@example.com'}{' '}
                            <span className="text-gray-400 font-normal">(billing email)</span>
                          </span>
                        </div>
                        <div className="pt-2 text-gray-600 border-t border-gray-100 leading-relaxed">
                          {invitePreviewClient?.body_preview ||
                            `${settingsName || org?.name || 'Your insurance partner'} has invited you to complete a tabletop exercise on Flash. Use the link in this email to register and get started.`}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="flex gap-2">
                          <span className="text-gray-400 font-medium">Subject</span>
                          <span className="font-bold text-[#0c1e40]">
                            {invitePreviewTeam?.subject || `Join ${settingsName || org?.name || 'your team'} on Flash Emergency Management`}
                          </span>
                        </div>
                        <div className="pt-2 text-gray-600 border-t border-gray-100 leading-relaxed">
                          {invitePreviewTeam?.body_preview ||
                            `You have been invited to join ${settingsName || org?.name || 'your organization'} on Flash. Sign in to collaborate with your organization.`}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Section 3: Security */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h2 className="text-base font-bold text-[#0c1e40]">Security</h2>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Two-factor sign-in for your team and the clients you invite.
                </p>
              </div>
              <div className="md:col-span-2">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Optional Option */}
                  <div
                    onClick={() => setSettingsMfaPolicy('optional')}
                    className={`cursor-pointer rounded-xl p-4 transition-all flex items-start gap-3 border ${
                      settingsMfaPolicy === 'optional'
                        ? 'border-[#991b1b] bg-white ring-1 ring-[#991b1b]'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="mt-0.5">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        settingsMfaPolicy === 'optional' ? 'border-[#991b1b]' : 'border-gray-300'
                      }`}>
                        {settingsMfaPolicy === 'optional' && (
                          <div className="w-2 h-2 rounded-full bg-[#991b1b]"></div>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Optional</p>
                      <p className="text-xs text-gray-500 mt-1">
                        People can turn on two-factor sign-in themselves.
                      </p>
                    </div>
                  </div>

                  {/* Required Option */}
                  <div
                    onClick={() => setSettingsMfaPolicy('mandatory')}
                    className={`cursor-pointer rounded-xl p-4 transition-all flex items-start gap-3 border ${
                      settingsMfaPolicy === 'mandatory'
                        ? 'border-[#991b1b] bg-white ring-1 ring-[#991b1b]'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="mt-0.5">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${
                        settingsMfaPolicy === 'mandatory' ? 'border-[#991b1b]' : 'border-gray-300'
                      }`}>
                        {settingsMfaPolicy === 'mandatory' && (
                          <div className="w-2 h-2 rounded-full bg-[#991b1b]"></div>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-bold text-gray-900">Required</p>
                      <p className="text-xs text-gray-500 mt-1">
                        Everyone must set up an authenticator app at their next sign-in.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Email delivery */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h2 className="text-base font-bold text-[#0c1e40]">Email delivery</h2>
                <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                  Check that invites reach inboxes before you send them to clients.
                </p>
              </div>
              <div className="md:col-span-2">
                <label htmlFor="settings-test-email" className="block text-xs font-semibold text-gray-700 mb-1">
                  Send a test email to
                </label>
                <div className="flex gap-2">
                  <input
                    id="settings-test-email"
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0c1e40]"
                  />
                  <button
                    type="button"
                    disabled={testEmailSending || !testEmail.trim()}
                    onClick={async () => {
                      setTestEmailSending(true);
                      setError(null);
                      try {
                        await insurerService.sendTestEmail(testEmail.trim());
                        setMessage('Test email sent successfully');
                      } catch (err: any) {
                        setError(err?.response?.data?.detail?.detail || err?.response?.data?.detail || 'Test email failed');
                      } finally {
                        setTestEmailSending(false);
                      }
                    }}
                    className="px-4 py-2 text-xs font-semibold rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-50 transition-colors"
                  >
                    {testEmailSending ? 'Sending…' : 'Send test'}
                  </button>
                </div>
              </div>
            </div>

            {/* Section 5: Transfer admin role */}
            <div className="bg-[#fff8f8] rounded-xl border border-red-200 p-6 shadow-sm grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h2 className="text-base font-bold text-[#991b1b]">Transfer admin role</h2>
                <p className="text-xs text-gray-600 mt-1 leading-relaxed">
                  Hand the admin role to another active teammate. You&apos;ll become a member.
                </p>
              </div>
              <div className="md:col-span-2 space-y-2">
                <div className="flex gap-2">
                  <select
                    value={transferAdminUserId}
                    onChange={(e) =>
                      setTransferAdminUserId(e.target.value ? Number(e.target.value) : '')
                    }
                    className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#991b1b]"
                  >
                    <option value="">
                      {team.filter(
                        (m) =>
                          m.id !== user?.id &&
                          m.is_active &&
                          m.is_verified &&
                          m.org_role !== 'org_admin'
                      ).length === 0
                        ? 'No other active teammates'
                        : 'Select active teammate…'}
                    </option>
                    {team
                      .filter(
                        (m) =>
                          m.id !== user?.id &&
                          m.is_active &&
                          m.is_verified &&
                          m.org_role !== 'org_admin'
                      )
                      .map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.full_name ? `${m.full_name} (${m.email})` : m.email}
                        </option>
                      ))}
                  </select>
                  <button
                    type="button"
                    disabled={transferAdminUserId === ''}
                    onClick={handleTransferAdmin}
                    className="px-4 py-2 text-xs font-semibold rounded-lg bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 shadow-sm disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    Transfer
                  </button>
                </div>
                <p className="text-xs text-gray-500">
                  <button
                    type="button"
                    onClick={() => navigate('/insurer-dashboard?tab=team')}
                    className="text-blue-600 hover:underline font-medium"
                  >
                    Invite a teammate
                  </button>{' '}
                  first — they need to accept before you can transfer.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                disabled={settingsSubmitting}
                className="px-6 py-2.5 text-sm font-semibold rounded-lg bg-[#991b1b] text-white hover:bg-[#7f1d1d] shadow-sm disabled:opacity-50 transition-colors"
              >
                {settingsSubmitting ? 'Saving…' : 'Save settings'}
              </button>
            </div>
          </form>
        )}

        {/* ───────────── CLIENT DETAIL MODAL ───────────── */}
        {selectedClientId !== null && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-start mb-4 border-b border-gray-100 pb-3">
                <h3 className="text-lg font-bold text-[#0c1e40]">Client detail</h3>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedClientId(null);
                    setClientDetail(null);
                  }}
                  className="text-gray-400 hover:text-gray-700 font-bold text-lg"
                >
                  &times;
                </button>
              </div>
              {clientDetailLoading ? (
                <div className="py-8 text-center text-sm text-gray-500">Loading details…</div>
              ) : clientDetail ? (
                <div className="space-y-6 text-sm">
                  <div className="border-b border-gray-100 pb-4">
                    {clientDetail.client.full_name && (
                      <p className="text-base font-bold text-[#0c1e40]">{clientDetail.client.full_name}</p>
                    )}
                    <p className="text-gray-600 text-xs mt-0.5">{clientDetail.client.email}</p>
                    <dl className="mt-3 grid grid-cols-2 gap-4">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <dt className="text-xs font-semibold uppercase text-gray-500">Credits remaining</dt>
                        <dd className="text-lg font-bold text-[#0c1e40] mt-0.5">{clientDetail.client.credits_remaining}</dd>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <dt className="text-xs font-semibold uppercase text-gray-500">Exercises completed</dt>
                        <dd className="text-lg font-bold text-[#0c1e40] mt-0.5">{clientDetail.client.exercises_used}</dd>
                      </div>
                    </dl>
                  </div>

                  {clientDetail.purchases.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Token grants</h4>
                      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                        {clientDetail.purchases.map((p) => (
                          <li key={p.id} className="p-3 text-xs">
                            <div className="flex items-center justify-between">
                              <span className="font-bold text-[#0c1e40]">{formatTokenTier(p.partner_token_tier)}</span>
                              <div className="flex items-center gap-2">
                                <span className="text-gray-500">{formatPurchaseStatus(p.status)}</span>
                                <span className="font-semibold text-gray-900">{formatCents(p.amount_cents)}</span>
                              </div>
                            </div>
                            <p className="mt-1 text-gray-500">
                              {p.credits_remaining} of {p.credits_total} remaining &bull; Granted {formatShortDate(p.created_at)}
                            </p>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {clientDetail.exercises.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Exercises</h4>
                      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                        {clientDetail.exercises.map((ex) => (
                          <li key={ex.id} className="p-3 text-xs flex justify-between items-center">
                            <div>
                              <p className="font-bold text-[#0c1e40]">{ex.exercise_name || `Exercise #${ex.id}`}</p>
                              <p className="text-gray-500 mt-0.5">Created {formatShortDate(ex.created_at)}</p>
                            </div>
                            <span className="px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-700">
                              {formatExerciseStatus(ex.status)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {clientDetail.ledger_entries && clientDetail.ledger_entries.length > 0 && (
                    <div>
                      <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Billing ledger</h4>
                      <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                        {clientDetail.ledger_entries.map((e) => (
                          <li key={e.id} className="p-3 text-xs flex justify-between items-center">
                            <div>
                              <p className="font-bold text-[#0c1e40]">{formatLedgerEntryType(e.entry_type)}</p>
                              <p className="text-gray-500 mt-0.5">{formatShortDate(e.created_at)}</p>
                            </div>
                            <div className="text-right">
                              <p className="font-semibold text-gray-900">{formatCents(e.amount_cents)}</p>
                              <span className="text-gray-500 text-[11px]">{formatLedgerStatus(e.status)}</span>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* ───────────── REMOVE TEAM CONFIRM MODAL ───────────── */}
        {removeTeamConfirm.open && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
              <h3 className="text-lg font-bold text-[#0c1e40]">Remove team member?</h3>
              <p className="text-sm text-gray-600 mt-2">
                Remove <span className="font-semibold text-gray-900">{removeTeamConfirm.email}</span> from your team? They will lose access to this organization.
              </p>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setRemoveTeamConfirm({ open: false, userId: null, email: '' })}
                  disabled={removingId !== null}
                  className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmRemoveTeamMember}
                  disabled={removingId !== null}
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-[#991b1b] text-white hover:bg-[#7f1d1d] disabled:opacity-50"
                >
                  {removingId !== null ? 'Removing…' : 'Remove'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Client Row Action Menu Portal */}
        {activeClientMenuId !== null && clientMenuPosition && (() => {
          const activeClient = clients.find((c) => c.id === activeClientMenuId);
          if (!activeClient) return null;
          return createPortal(
            <div className="client-menu-portal">
              <div
                className="fixed inset-0 z-40 bg-transparent"
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveClientMenuId(null);
                  setClientMenuPosition(null);
                }}
              />
              <div
                style={{
                  position: 'fixed',
                  ...(clientMenuPosition.top !== undefined ? { top: `${clientMenuPosition.top}px` } : {}),
                  ...(clientMenuPosition.bottom !== undefined ? { bottom: `${clientMenuPosition.bottom}px` } : {}),
                  left: `${clientMenuPosition.left}px`,
                }}
                className="w-44 bg-white rounded-lg border border-gray-200 shadow-xl py-1 z-50 text-left"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => {
                    setActiveClientMenuId(null);
                    setClientMenuPosition(null);
                    openClientDetail(activeClient.id);
                  }}
                  className="w-full px-4 py-2 text-xs font-medium text-gray-700 hover:bg-gray-100 flex items-center gap-2 text-left"
                >
                  View details
                </button>
                {activeClient.credits_remaining > 0 && activeClient.exercises_used === 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setActiveClientMenuId(null);
                      setClientMenuPosition(null);
                      setRevokeTokenClient(activeClient);
                    }}
                    className="w-full px-4 py-2 text-xs font-medium text-amber-700 hover:bg-amber-50 flex items-center gap-2 text-left"
                  >
                    Revoke credit
                  </button>
                )}
                <div className="my-1 border-t border-gray-100"></div>
                <button
                  type="button"
                  onClick={() => {
                    setActiveClientMenuId(null);
                    setClientMenuPosition(null);
                    handleUnlinkClient(activeClient);
                  }}
                  className="w-full px-4 py-2 text-xs font-medium text-red-600 hover:bg-red-50 flex items-center gap-2 text-left"
                >
                  Unlink client
                </button>
              </div>
            </div>,
            document.body
          );
        })()}

        {/* Modals for Grant / Revoke Token */}
        <GrantTokenModal
          isOpen={Boolean(grantTokenClient)}
          client={grantTokenClient}
          onClose={() => setGrantTokenClient(null)}
          onGranted={handleGrantTokenSuccess}
          onError={setError}
        />

        <RevokeTokenModal
          isOpen={Boolean(revokeTokenClient)}
          client={revokeTokenClient}
          onClose={() => setRevokeTokenClient(null)}
          onRevoked={handleRevokeTokenSuccess}
          onError={setError}
        />
      </div>
    </div>
  );
};

export default InsurerDashboard;
